import Modal from "@/components/controls/Modal";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { usePeopleInfinite } from "@/entities/people/hooks";
import {
  CheckboxGroup,
  CheckboxGroupItem
} from "@/components/ui/checkbox-group";
import { getFullName } from "@/entities/people/helpers";
import SearchInput from "@/components/ui/search-input";
import { useCompanies } from "@/pages/companies/hooks";
import { CreateQuestionnaireQuestionSettings } from "@/pages/deals/types";
import { emptyQuestion } from "@/pages/deals/helpers";
import {
  useCreateQuestionMutation,
  useUpdateDealQuestionnaireMutation
} from "@/pages/deals/hooks";
import InlineLoader from "@/components/ui/inline-loader";
import { useInfiniteScrollTrigger } from "@/hooks/useInfiniteScrollTrigger";
import { filter, includes, isEmpty, isNull, map, union, without } from "lodash";

let lastSubmissionTime = 0;
const SUBMISSION_DEBOUNCE_MS = 2000; // 2 second debounce

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityId: string;
  companyId: string;
  approverPeopleIds: string[];
  dealId: string;
  questionnaireId: string;
  resetQuestionnaire?: () => Promise<void>;
}

const AddApproversModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  companyId,
  approverPeopleIds,
  dealId,
  questionnaireId,
  resetQuestionnaire
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPeople, setSelectedPeople] = useState<string[]>(
    approverPeopleIds || []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createQuestion = useCreateQuestionMutation();
  const updateQuestionnaire = useUpdateDealQuestionnaireMutation();

  const {
    entities: people,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isError
  } = usePeopleInfinite(
    { companyId },
    searchTerm ? { term: searchTerm } : undefined,
    undefined,
    undefined,
    "firstName"
  );

  const {
    entities: [companyData]
  } = useCompanies([companyId]);

  const { rootRef, sentinelRef } = useInfiniteScrollTrigger({
    canLoadMore: !!hasNextPage && !isFetchingNextPage && !isError,
    onLoadMore: fetchNextPage,
    itemCount: people?.length
  });

  useEffect(() => {
    if (isOpen) {
      setSelectedPeople(approverPeopleIds || []);
      setIsSubmitting(false);
      lastSubmissionTime = 0;
    } else {
      setSearchTerm("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleCancel = () => {
    if (isSubmitting) return;
    setIsOpen(false);
  };

  const handleAddApprovers = async () => {
    const now = Date.now();

    if (now - lastSubmissionTime < SUBMISSION_DEBOUNCE_MS) {
      return;
    }

    lastSubmissionTime = now;
    setIsSubmitting(true);

    try {
      const newApproversData = filter(
        selectedPeople,
        (personId) => !includes(approverPeopleIds, personId)
      ).map((personId) => people?.find((p) => p.id === personId));

      if (newApproversData.length === 0) {
        setIsOpen(false);
        return;
      }

      await Promise.all(
        newApproversData.map(async (person) => {
          const newQuestion: CreateQuestionnaireQuestionSettings = {
            ...emptyQuestion,
            questionnaireId,
            questionText: person ? getFullName(person) : "",
            dealId,
            responseType: {
              type: null,
              setValueType: null,
              unit: null,
              value: "bool"
            }
          };

          const questionData = await createQuestion.mutateAsync({
            data: newQuestion
          });

          await updateQuestionnaire.mutateAsync({
            pathParams: { dealId, questionnaireId },
            data: {
              questions: [
                {
                  questionId: questionData?.id,
                  response: {
                    value: null,
                    person
                  }
                }
              ]
            }
          });
        })
      );

      setIsOpen(false);
    } catch (error) {
      console.error("Error adding approvers:", error);
    } finally {
      setIsSubmitting(false);
      await resetQuestionnaire?.();
    }
  };

  const handleSelectedPeopleChange = (personId: string, checked: boolean) => {
    setSelectedPeople((prevPeople) =>
      checked ? union(prevPeople, [personId]) : without(prevPeople, personId)
    );
  };

  const newSelectedPeople = filter(
    selectedPeople,
    (personId) => !includes(approverPeopleIds, personId)
  );

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[35rem]"
      footerClassname="border-t-[0.5px] border-border-border1 pt-1"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {`${companyData?.name} ${t("people.title")}`}
        </Text>
      }
      content={
        <div className="flex flex-col gap-3 w-[35rem] p-4 h-[60vh] overflow-y-auto">
          <SearchInput
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            placeholder={t("deals.dealView.approversModal.placeholder")}
          />
          <div
            ref={rootRef}
            className="flex flex-col gap-3 px-4 flex-1 overflow-y-auto"
          >
            <CheckboxGroup fullWidth color="var(--cta)">
              {map(people, (person) => (
                <CheckboxGroupItem
                  key={person.id}
                  id={person.id}
                  label={
                    <div className="flex gap-2">
                      <Text>{getFullName(person)}</Text>
                      {!isNull(person.jobTitle) && (
                        <Text variant="bodySecondary">{person.jobTitle}</Text>
                      )}
                    </div>
                  }
                  checked={includes(selectedPeople, person.id)}
                  onCheckedChange={(checked: boolean) =>
                    handleSelectedPeopleChange(person.id, checked)
                  }
                  disabled={includes(approverPeopleIds, person.id)}
                />
              ))}
            </CheckboxGroup>
            <div ref={sentinelRef} className="h-px shrink-0" />
          </div>
          <div className="w-full text-center">
            {(isLoading || isFetchingNextPage) && (
              <InlineLoader label={t("loading")} />
            )}
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            data-test="cancel-button"
            variant="secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {t("cancel")}
          </Button>
          <Button
            data-test="submit-button"
            disabled={isEmpty(newSelectedPeople) || isSubmitting}
            onClick={handleAddApprovers}
          >
            {t("deals.dealView.approversModal.submit")}
          </Button>
        </div>
      }
    />
  );
};

export default AddApproversModal;
